/* DAILE v2 (dv) — announcement strip (sections/dv-announcement.liquid).
 *
 * Module "dv-announcement":
 *   - publishes the strip's real height as --announcement-bar-height on :root
 *     (Prestige and daile-collection-hero subtract it from first-screen
 *     heights); removed again when the section unloads in the theme editor;
 *   - free-shipping amount in the shopper's currency: the threshold (shop
 *     currency, EUR) x Shopify.currency.rate, rounded UP to a round number
 *     (two significant digits; steps of 5 for two-digit amounts), formatted
 *     with the current money format (shop.money_format, as the prices on the
 *     page). Same currency, or no rate: the Liquid text stays (€100);
 *   - marquee (Web Animations API, a compositor transform): the track moves by
 *     exactly one group of messages; copies (aria-hidden, inert) are added
 *     until the track covers the strip twice. Speed = data-dv-speed px/s.
 *     RTL moves the other way. WCAG 2.2.2 without a button (round 6, owner:
 *     "remove the pause button"): it pauses while a mouse is over the strip,
 *     while focus is inside it (the strip itself is a Tab stop once the JS
 *     runs, tabindex="0"), while it is off screen or the tab is hidden; a tap
 *     or click on the strip (not on a link in it) stops it for good, the
 *     next one starts it again (.dv-announcement--stopped). Width changes
 *     (resize, fonts) keep the current position;
 *   - prefers-reduced-motion: one message at a time, every 5 s, no movement
 *     (hover, focus and a tap stop the rotation too). The mode follows the
 *     preference live;
 *   - theme editor: a selected block is shown on its own (rotation mode,
 *     no timer) until it is deselected.
 */
(() => {
  'use strict';

  if (!window.DV || typeof window.DV.register !== 'function') return;

  const DV = window.DV;
  const html = document.documentElement;
  const ROTATE_MS = 5000;

  // Round up to two significant digits; two-digit amounts to a multiple of 5.
  const roundUp = (value) => {
    if (!(value > 0)) return value;
    const digits = Math.floor(Math.log10(value)) + 1;
    let step = 1;
    if (digits === 2) step = 5;
    else if (digits > 2) step = 10 ** (digits - 2);
    return Math.ceil(value / step - 1e-9) * step;
  };

  // Shopify money format ("${{amount}}", "{{amount_with_comma_separator}} kr", …)
  // for a whole number.
  const formatMoney = (value, format, currency) => {
    const group = (n, sep) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const match = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
    if (!match) {
      try {
        return new Intl.NumberFormat((window.Shopify && window.Shopify.locale) || undefined, {
          style: 'currency', currency, numberingSystem: 'latn', maximumFractionDigits: 0,
        }).format(value);
      } catch (error) {
        return `${value} ${currency}`;
      }
    }
    let amount;
    switch (match[1]) {
      case 'amount_with_comma_separator':
      case 'amount_no_decimals_with_comma_separator':
        amount = group(value, '.');
        break;
      case 'amount_with_space_separator':
      case 'amount_no_decimals_with_space_separator':
      case 'amount_with_period_and_space_separator':
        amount = group(value, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        amount = group(value, "'");
        break;
      default:
        amount = group(value, ',');
    }
    return format.replace(match[0], amount);
  };

  DV.register('dv-announcement', (root) => {
    const bar = DV.queryAll(root, '[data-dv-announcement]')[0];
    if (!bar) return undefined;

    const viewport = bar.querySelector('.dv-announcement__viewport');
    const track = bar.querySelector('[data-dv-ann-track]');
    const group = bar.querySelector('[data-dv-ann-group]');
    const cleanups = [];
    if (!track || !group || !viewport) return undefined;

    /* --announcement-bar-height ----------------------------------------- */
    const publish = () => {
      const height = bar.getBoundingClientRect().height;
      if (height > 0) html.style.setProperty('--announcement-bar-height', `${height.toFixed(2)}px`);
    };

    /* free-shipping amount ------------------------------------------------ */
    const base = bar.getAttribute('data-dv-base-currency') || 'EUR';
    const shopify = window.Shopify || {};
    const active = (shopify.currency && shopify.currency.active) || bar.getAttribute('data-dv-currency') || base;
    const rate = parseFloat(shopify.currency && shopify.currency.rate);
    if (active !== base && rate > 0) {
      bar.querySelectorAll('[data-dv-ann-amount]').forEach((node) => {
        const threshold = parseFloat(node.getAttribute('data-dv-ann-amount'));
        if (!(threshold > 0)) return;
        node.textContent = formatMoney(roundUp(threshold * rate), bar.getAttribute('data-dv-money-format'), active);
      });
    }

    /* state ---------------------------------------------------------------- */
    const reduced = DV.reducedMotion;
    const rtl = () => getComputedStyle(bar).direction === 'rtl';
    const speed = Math.max(8, parseFloat(bar.getAttribute('data-dv-speed')) || 32);
    const holds = new Set(); // hover / focus / offscreen / hidden / user / editor
    let animation = null;
    let shift = 0;
    let rotateTimer = 0;
    let current = 0;
    let editing = null;

    const messages = () => [...group.querySelectorAll('.dv-announcement__message')];
    const held = () => holds.size > 0;

    /* marquee ------------------------------------------------------------- */
    const clones = () => [...track.querySelectorAll('[data-dv-ann-clone]')];

    const fill = () => {
      const width = group.getBoundingClientRect().width;
      if (!width) return 0;
      // Copies until the track covers the viewport plus one group.
      const needed = Math.max(1, Math.ceil(viewport.clientWidth / width));
      let list = clones();
      while (list.length < needed) {
        const copy = list[0].cloneNode(true);
        track.append(copy);
        list = clones();
      }
      return width;
    };

    const syncPlayback = () => {
      if (!animation) return;
      if (held()) {
        if (animation.playState === 'running') animation.pause();
      } else if (animation.playState !== 'running') {
        animation.play();
      }
    };

    const startMarquee = () => {
      const width = fill();
      if (!width) return;
      const distance = width * (rtl() ? 1 : -1);
      if (animation && Math.abs(distance - shift) < 0.5) return;
      const progress = animation && animation.effect ? (animation.effect.getComputedTiming().progress || 0) : 0;
      if (animation) animation.cancel();
      shift = distance;
      const duration = (Math.abs(width) / speed) * 1000;
      animation = track.animate(
        [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${distance}px, 0, 0)` }],
        { duration, iterations: Infinity, easing: 'linear' },
      );
      animation.currentTime = progress * duration;
      syncPlayback();
    };

    const stopMarquee = () => {
      if (animation) animation.cancel();
      animation = null;
      shift = 0;
    };

    /* rotation (reduced motion / editor) --------------------------------- */
    const show = (index) => {
      const list = messages();
      if (!list.length) return;
      current = ((index % list.length) + list.length) % list.length;
      list.forEach((item, i) => item.classList.toggle('is-current', i === current));
    };

    const scheduleRotation = () => {
      window.clearTimeout(rotateTimer);
      rotateTimer = 0;
      if (!bar.classList.contains('dv-announcement--rotate') || editing || held() || messages().length < 2) return;
      rotateTimer = window.setTimeout(() => {
        show(current + 1);
        scheduleRotation();
      }, ROTATE_MS);
    };

    /* mode ------------------------------------------------------------------ */
    const applyMode = () => {
      const rotate = reduced.matches || !!editing;
      bar.classList.toggle('dv-announcement--rotate', rotate);
      bar.classList.toggle('dv-announcement--marquee', !rotate);
      bar.classList.add('dv-announcement--ready');
      if (rotate) {
        stopMarquee();
        show(editing ? messages().indexOf(editing) : current);
        scheduleRotation();
      } else {
        window.clearTimeout(rotateTimer);
        messages().forEach((item) => item.classList.remove('is-current'));
        startMarquee();
      }
      publish();
    };

    const onHoldChange = () => {
      syncPlayback();
      scheduleRotation();
    };
    const hold = (reason, on) => {
      if (on) holds.add(reason);
      else holds.delete(reason);
      onHoldChange();
    };

    /* tap / click to stop (WCAG 2.2.2) --------------------------------------- */
    // The strip is a Tab stop (focus pauses it), so keyboard users can stop it
    // too; it is added here, so without JS (a static row) there is none.
    const hadTabindex = bar.hasAttribute('tabindex');
    if (!hadTabindex) bar.setAttribute('tabindex', '0');
    const onClick = (event) => {
      if (event.target instanceof Element && event.target.closest('a[href]')) return;
      const stopped = !holds.has('user');
      bar.classList.toggle('dv-announcement--stopped', stopped);
      hold('user', stopped);
    };
    bar.addEventListener('click', onClick);
    cleanups.push(() => {
      bar.removeEventListener('click', onClick);
      bar.classList.remove('dv-announcement--stopped');
      if (!hadTabindex) bar.removeAttribute('tabindex');
    });

    /* hover / focus ----------------------------------------------------------- */
    const onEnter = (event) => {
      if (event.pointerType === 'mouse') hold('hover', true);
    };
    const onLeave = () => hold('hover', false);
    // Keyboard focus only: a click or tap also focuses the strip, and that
    // must not keep it paused after a second tap has started it again.
    const onFocusIn = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      let keyboard = true;
      try {
        keyboard = !target || target.matches(':focus-visible');
      } catch (error) {
        keyboard = true;
      }
      hold('focus', keyboard);
    };
    const onFocusOut = (event) => {
      if (!(event.relatedTarget instanceof Node) || !bar.contains(event.relatedTarget)) hold('focus', false);
    };
    bar.addEventListener('pointerenter', onEnter);
    bar.addEventListener('pointerleave', onLeave);
    bar.addEventListener('focusin', onFocusIn);
    bar.addEventListener('focusout', onFocusOut);
    cleanups.push(() => {
      bar.removeEventListener('pointerenter', onEnter);
      bar.removeEventListener('pointerleave', onLeave);
      bar.removeEventListener('focusin', onFocusIn);
      bar.removeEventListener('focusout', onFocusOut);
    });

    /* off screen / hidden tab --------------------------------------------------- */
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => hold('offscreen', !entry.isIntersecting));
      });
      io.observe(bar);
      cleanups.push(() => io.disconnect());
    }
    const onVisibility = () => hold('hidden', document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    cleanups.push(() => document.removeEventListener('visibilitychange', onVisibility));

    /* size ----------------------------------------------------------------------- */
    let frame = 0;
    const onResize = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        publish();
        if (bar.classList.contains('dv-announcement--marquee')) startMarquee();
      });
    };
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(onResize);
      observer.observe(bar);
      observer.observe(group);
      cleanups.push(() => observer.disconnect());
    } else {
      window.addEventListener('resize', onResize);
      cleanups.push(() => window.removeEventListener('resize', onResize));
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(onResize).catch(() => {});

    cleanups.push(DV.onMediaChange(reduced, applyMode));
    applyMode();

    return {
      destroy() {
        cleanups.forEach((fn) => fn());
        if (frame) cancelAnimationFrame(frame);
        window.clearTimeout(rotateTimer);
        stopMarquee();
        html.style.removeProperty('--announcement-bar-height');
      },
      onBlockSelect(block) {
        if (!group.contains(block)) return;
        editing = block;
        applyMode();
      },
      onBlockDeselect() {
        editing = null;
        applyMode();
      },
    };
  });
})();
